/**
 * 應收沖帳(REQ-20260806002)。
 *
 * 需求:「需讀取客戶主檔的結帳週期(如月結、發票選單),並支援一鍵導出或列印
 * 單一客戶的『月結對帳單』方便向客戶請款。」
 *
 * 選客戶 → 上半顯示未結明細(哪幾張單還沒收完)、下半是可列印的月結對帳單。
 *
 * REQ-20260815001:也接受深連結(訪談頁的「收款」徽章)——
 * 客戶與那張單都先幫使用者選好,不用再打一次客戶編號。
 */
import { useEffect, useRef, useState } from 'react';
import type { PartyInfo } from '@app/shared';
import { ExportJpegButton } from '../../components/ExportJpegButton';
import { PartyPicker } from '../../components/PartyPicker';
import { buildDocumentFilename } from '../../lib/filename';
import { formatMoney } from '../../lib/format-money';
import {
  useArOutstanding,
  useCustomerStatement,
  usePartyById,
  usePayerNames,
  usePersonalOutstanding,
} from '../accounting/api';
import { CustomerArPanel } from '../settlements/CustomerArPanel';
import { ReceiptModal } from './ReceiptModal';
import { PersonalOutstandingView, StatementView } from './StatementView';

export function ReceivablePanel({
  initialCustomerId,
  initialOrderNo,
}: {
  /** 深連結帶進來的客戶:掛載後自動選起來(REQ-20260815001) */
  initialCustomerId?: number | undefined;
  /** 深連結帶進來的單號:客戶的未結明細回來後自動開登錄收款並預先勾這一筆 */
  initialOrderNo?: string | undefined;
}): JSX.Element {
  const [customer, setCustomer] = useState<PartyInfo | null>(null);
  const [period, setPeriod] = useState(currentPeriodInTaipei());
  /**
   * 自訂區間(REQ-20260815013)。舊會計系統的請款明細是「任意起訖日期」,
   * 但月結客戶多半還是照月份查 —— 兩種都留著,預設維持既有的月份查詢。
   */
  const [customRange, setCustomRange] = useState(false);
  const [from, setFrom] = useState(() => `${currentPeriodInTaipei()}-01`);
  const [to, setTo] = useState(todayInTaipei);
  const [receiptOpen, setReceiptOpen] = useState(false);
  /**
   * REQ-20260810002:帳款對象篩選。null = 公司公帳(月結對帳單,後端已排除私帳單)。
   * 選了某個人就整頁切換成他的個人未付明細 —— 兩者是不同的請款對象,
   * 混在同一張表上只會讓人搞不清楚這筆錢要跟誰收。
   */
  const [payerName, setPayerName] = useState<string | null>(null);

  const outstanding = useArOutstanding(customer?.id);
  const statement = useCustomerStatement(customer?.id, customRange ? { from, to } : { period });
  const payerNames = usePayerNames(customer?.id);
  const personal = usePersonalOutstanding(customer?.id, payerName);

  // 公司公帳的未結明細**不需要前端再過濾**:私帳單是否列進來由後端決定
  // (登錄收款要沖得到私帳單,所以 ar-outstanding 仍然全列;月結對帳單才排除)。
  const outstandingView = payerName ? personal : outstanding;

  /**
   * 深連結:客戶已經由呼叫端指定,用 id 撈回整包主檔後走跟使用者自己挑一樣的
   * `setCustomer()`(REQ-20260815001)。資料還沒回來之前畫面維持「先選一個客戶」,
   * 不會半途開彈窗。查無此客戶時 items 是空的,等於沒帶深連結。
   */
  const deepLinkParty = usePartyById('customer', initialCustomerId);
  const customerApplied = useRef(false);
  useEffect(() => {
    if (customerApplied.current || customer) return;
    const found = deepLinkParty.data?.items[0];
    if (!found) return;
    customerApplied.current = true;
    setCustomer(found);
  }, [customer, deepLinkParty.data]);

  /**
   * 客戶就位、未結明細也回來之後,自動打開登錄收款(單號本身交給 ReceiptModal
   * 去勾)。同樣只做一次 —— 使用者關掉彈窗後不該再被拉回來。
   */
  const receiptApplied = useRef(false);
  /**
   * 「這個彈窗是深連結自動開的」(REQ-20260824003)。深連結一律開新分頁,
   * 這個分頁的目的就是完成這一次收款登錄,關掉彈窗時順手把分頁收掉,
   * 讓焦點自然回到原本那個還開著的訪談紀錄分頁。
   * 使用者自己按「登錄收款」開的維持 false —— 那是他唯一的工作分頁,不能關。
   */
  const deepLinkReceiptRef = useRef(false);
  useEffect(() => {
    if (receiptApplied.current || !initialOrderNo || !customer) return;
    if (!outstanding.data) return;
    receiptApplied.current = true;
    deepLinkReceiptRef.current = true;
    setReceiptOpen(true);
  }, [initialOrderNo, customer, outstanding.data]);

  /*
   * 存檔預設檔名(REQ-20260903006)。本頁原本從來沒設過 `document.title`,
   * 對帳單存出來都叫「訪談系統」—— 做法照抄 REQ-20260811002 的訂單列印頁:
   * 設 `document.title`(不引 PDF 套件),cleanup 還原成進來之前的值。
   *
   * 選了帳款對象時畫面換成**個人未付帳款明細**,那份資料不吃期間條件
   * (`usePersonalOutstanding` 沒有 period 參數)—— 檔名要是接上月份就是假資訊,
   * 所以那個分支改接對象姓名。日期段落的連字號要去掉,否則 `-` 同時是段落分隔符
   * 又出現在段落裡面,`對帳單-金樂印-2026-09` 看不出哪裡是分隔(沿用
   * `report-filename.ts` 的日期寫法)。
   */
  const periodSegment = customRange
    ? `${from.replaceAll('-', '')}-${to.replaceAll('-', '')}`
    : period.replaceAll('-', '');
  const documentFilename = buildDocumentFilename([
    '對帳單',
    customer?.simpleName,
    payerName ?? periodSegment,
  ]);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = documentFilename;
    return () => {
      document.title = previousTitle;
    };
  }, [documentFilename]);

  /** 「另存 JPEG」要截的那一塊:對帳單 / 個人未付明細共用的 `.print-area` */
  const sheetRef = useRef<HTMLDivElement>(null);

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-3 no-print">
        {/* 換客戶時一定要把帳款對象清掉 —— 姓名清單是逐客戶查的,留著會查到別人的空清單 */}
        <PartyPicker
          type="customer"
          value={customer}
          onChange={(p) => {
            setCustomer(p);
            setPayerName(null);
          }}
        />
        <div className="grid gap-1">
          <div className="flex items-center justify-between gap-2">
            <label className="text-sm text-slate-600" htmlFor={customRange ? 'stmt-from' : 'stmt-period'}>
              對帳期間
            </label>
            <label className="flex items-center gap-1 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={customRange}
                onChange={(e) => setCustomRange(e.target.checked)}
                disabled={payerName != null}
                data-testid="stmt-custom-range"
              />
              自訂區間
            </label>
          </div>
          {customRange ? (
            <div className="flex items-center gap-1">
              <input
                id="stmt-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                disabled={payerName != null}
                aria-label="起日"
                className="border rounded-md px-2 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              />
              <span className="text-slate-400">~</span>
              <input
                id="stmt-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={payerName != null}
                aria-label="迄日"
                className="border rounded-md px-2 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              />
            </div>
          ) : (
            <input
              id="stmt-period"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={payerName != null}
              className="border rounded-md px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
            />
          )}
        </div>
        <div className="grid gap-1">
          <label className="text-sm text-slate-600" htmlFor="stmt-payer">
            帳款對象
          </label>
          <select
            id="stmt-payer"
            value={payerName ?? ''}
            onChange={(e) => setPayerName(e.target.value === '' ? null : e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
            data-testid="stmt-payer-filter"
          >
            <option value="">公司公帳(月結對帳單)</option>
            {(payerNames.data ?? []).map((n) => (
              <option key={n} value={n}>
                {n}(私帳)
              </option>
            ))}
          </select>
        </div>
      </div>

      {!customer ? (
        <div className="text-slate-500 text-sm">
          先選一個客戶,就會列出他的未結應收明細與月結對帳單。
        </div>
      ) : (
        <>
          <section className="grid gap-2 no-print">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-700">
                {payerName ? `未結明細(${payerName} 私帳)` : '未結應收明細'}
              </h3>
              <button
                type="button"
                data-testid="receipt-open"
                onClick={() => setReceiptOpen(true)}
                disabled={(outstandingView.data ?? []).length === 0}
                className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                登錄收款
              </button>
            </div>
            {outstandingView.isLoading ? (
              <div role="status" className="text-slate-500 text-sm">
                載入中...
              </div>
            ) : outstandingView.isError ? (
              <div role="alert" className="text-red-600 text-sm">
                載入失敗：
                {outstandingView.error instanceof Error ? outstandingView.error.message : '未知錯誤'}
              </div>
            ) : (outstandingView.data ?? []).length === 0 ? (
              <div className="text-slate-500 text-sm">
                {payerName
                  ? `${payerName} 目前沒有未結的私帳款項。`
                  : '這個客戶目前沒有未結的應收款項。'}
              </div>
            ) : (
              <table className="w-full bg-white shadow-sm rounded-md">
                <thead>
                  <tr className="border-b text-left text-sm text-slate-600">
                    <th className="px-4 py-2">日期</th>
                    <th className="px-4 py-2">單號</th>
                    <th className="px-4 py-2 text-right">含稅總計</th>
                    <th className="px-4 py-2 text-right">已收</th>
                    <th className="px-4 py-2 text-right">折讓</th>
                    <th className="px-4 py-2 text-right">未結</th>
                  </tr>
                </thead>
                <tbody>
                  {(outstandingView.data ?? []).map((o) => (
                    <tr key={o.id} className="border-b last:border-0 hover:bg-slate-50 text-sm">
                      <td className="px-4 py-2 whitespace-nowrap">{o.txnDate}</td>
                      <td className="px-4 py-2 whitespace-nowrap font-mono">{o.orderNo}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatMoney(o.total)}</td>
                      <td className="px-4 py-2 text-right font-mono">
                        {formatMoney(o.settledAmount)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {formatMoney(o.allowanceAmount)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {formatMoney(o.outstanding)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/*
            已收的那一面:沿用客戶主控台「應收沖帳」分頁的同一支面板(REQ-20260815003)。
            這裡**刻意不吃 `payerName`** —— 沖帳紀錄是要看這個客戶的歷史全貌
            (公帳私帳都算),不是拿去跟客戶請款的文件,所以月結對帳單排除私帳的
            理由在這裡不成立。只在畫面上瀏覽,列印對帳單時不該跟著出去。
          */}
          <section id="settlement-history" className="grid gap-2 no-print">
            <h3 className="font-medium text-slate-700">收款沖帳紀錄</h3>
            <CustomerArPanel customerId={customer.id} />
          </section>

          <section className="grid gap-2" ref={sheetRef}>
            <div className="flex items-center justify-between no-print">
              <h3 className="font-medium text-slate-700">
                {payerName ? '個人未付帳款明細' : '月結對帳單'}
              </h3>
              <div className="flex items-center gap-3">
                {/* 同一份對帳單的第二種存檔格式(REQ-20260903006);PDF 那條路一行未動 */}
                <ExportJpegButton
                  targetRef={sheetRef}
                  filename={documentFilename}
                  disabled={payerName ? !personal.data : !statement.data}
                />
                <button
                  type="button"
                  onClick={() => window.print()}
                  disabled={payerName ? !personal.data : !statement.data}
                  className="border border-blue-600 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
                >
                  列印 / 匯出 PDF
                </button>
              </div>
            </div>

            {/*
              選了某個人 → 印他的個人未付明細;沒選 → 月結對帳單(後端已排除私帳單)。
              兩者共用同一個 `.print-area` 列印樣式,沒有引進 PDF 套件。
            */}
            {payerName ? (
              personal.isLoading ? (
                <div role="status" className="text-slate-500 text-sm">
                  載入中...
                </div>
              ) : personal.isError ? (
                <div role="alert" className="text-red-600 text-sm">
                  載入失敗：
                  {personal.error instanceof Error ? personal.error.message : '未知錯誤'}
                </div>
              ) : (
                <PersonalOutstandingView
                  customerName={customer.fullName}
                  payerName={payerName}
                  rows={personal.data ?? []}
                />
              )
            ) : statement.isLoading ? (
              <div role="status" className="text-slate-500 text-sm">
                載入中...
              </div>
            ) : statement.isError ? (
              <div role="alert" className="text-red-600 text-sm">
                載入失敗：
                {statement.error instanceof Error ? statement.error.message : '未知錯誤'}
              </div>
            ) : statement.data ? (
              <StatementView statement={statement.data} />
            ) : null}
          </section>

          {/*
            彈窗蓋住整頁,底下的沖帳紀錄看得到卻搆不著(REQ-20260815004)。
            先關彈窗讓那一段變成可見,再用 rAF 等關閉的 DOM 更新跑完才捲過去 ——
            同一個 tick 捲的話,目標還在被彈窗蓋住的版面裡,位置會算錯。
          */}
          <ReceiptModal
            open={receiptOpen}
            /*
              深連結開的彈窗:關掉時連分頁一起收(REQ-20260824003)。X / 取消 / ESC /
              送出成功都走這同一個 onClose,所以四種收尾方式行為一致。
              `window.close()` 是 best-effort —— 瀏覽器擋下來時它靜靜地什麼都不做,
              後面的 setReceiptOpen(false) 照常執行,退回原本「只關彈窗」的行為。
            */
            onClose={() => {
              if (deepLinkReceiptRef.current) window.close();
              setReceiptOpen(false);
            }}
            customerId={customer.id}
            outstanding={outstandingView.data ?? []}
            defaultPickedOrderNo={initialOrderNo}
            onViewHistory={() => {
              setReceiptOpen(false);
              requestAnimationFrame(() => {
                document
                  .getElementById('settlement-history')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              });
            }}
          />
        </>
      )}
    </div>
  );
}

/** 預設對帳期間 = 台北時區的當月 */
function currentPeriodInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
  })
    .format(new Date())
    .slice(0, 7);
}

/** 自訂區間的預設迄日 = 台北今天(容器 / 瀏覽器時區都不影響) */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
